import { Link, useNavigate } from 'react-router-dom';
import { CheckCheck, Megaphone } from 'lucide-react';

import type { Notification } from '@/api/notifications.api';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { NotificationRow } from '@/components/notifications/NotificationInbox';
import { sortUnreadFirst } from '@/components/notifications/notificationFormat';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationsInfinite,
} from '@/hooks/useNotifications';

/**
 * NotificationsPage — the full inbox at `/notifications` (Foundations
 * 2026-07-21). Reachable on its own (the orchestrator wires the route +
 * nav + the header bell separately). Cursor-paginated "load older",
 * unread-first ordering, and mark-all-read — reusing the same
 * `NotificationRow` the dropdown panel uses.
 */

export function NotificationsPage() {
  const navigate = useNavigate();
  const infinite = useNotificationsInfinite({ pageSize: 20 });
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  const items = infinite.data ? infinite.data.pages.flatMap((p) => p.items) : [];
  const ordered = sortUnreadFirst(items);
  const hasUnread = ordered.some((n) => n.readAt === null);

  function handleActivate(n: Notification) {
    if (n.readAt === null) markRead.mutate(n.id);
    if (n.linkPath) void navigate(n.linkPath);
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="typo-h1">Notifications</h1>
          <p className="mt-1 text-[14px] text-kw-muted">
            Everything sent directly to you.{' '}
            <Link
              to="/communication"
              className="inline-flex items-center gap-1 text-kw-cerulean hover:underline"
            >
              <Megaphone className="size-3.5" /> Announcements
            </Link>
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          disabled={!hasUnread || markAll.isPending}
          onClick={() => {
            markAll.mutate();
          }}
        >
          <CheckCheck className="mr-1.5 size-4" />
          Mark all read
        </Button>
      </div>

      {infinite.isLoading ? (
        <div className="flex justify-center py-16">
          <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
        </div>
      ) : infinite.error ? (
        <Card className="p-6 text-center text-[14px] text-kw-danger-text">
          Couldn&apos;t load your notifications.
        </Card>
      ) : ordered.length === 0 ? (
        <Card className="p-10 text-center">
          <p className="text-[14px] text-kw-muted">You&apos;re all caught up — nothing here yet.</p>
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <ul className="divide-y divide-kw-border">
            {ordered.map((n) => (
              <NotificationRow
                key={n.id}
                notification={n}
                onActivate={() => {
                  handleActivate(n);
                }}
              />
            ))}
          </ul>
        </Card>
      )}

      {infinite.hasNextPage ? (
        <div className="flex justify-center">
          <Button
            variant="secondary"
            size="sm"
            disabled={infinite.isFetchingNextPage}
            onClick={() => {
              void infinite.fetchNextPage();
            }}
          >
            {infinite.isFetchingNextPage ? 'Loading…' : 'Load older'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
